/**
 * Reduce flashing (spec 08 §4): every flash the renderer makes - the clock's and the pauses' light
 * pulse, the power heart's pink, lightning and bolt glows, fire's flicker, bursts, the boss's and a
 * respawning player's blink - goes through `capFlash`. With the option on, nothing repeats faster than
 * FLASH_CAP_HZ (WCAG 2.3.1's three flashes a second) and every swing keeps only FLASH_SOFTEN of its
 * strength. Pure: no three.js, no clock of its own; the renderer passes its time in.
 */

/** The fastest any flash may repeat with reduce flashing on, cycles per second. */
export const FLASH_CAP_HZ = 3;
/** The share of its swing (brightness, size, glow) a flash keeps with reduce flashing on. */
export const FLASH_SOFTEN = 0.35;

/** A repeating (or, with hz 0, one-off) flash: how often it repeats and how far it swings. */
export interface Flash {
  hz: number;
  depth: number;
}

/** The cap itself: a flash as it may be drawn. Off, it is untouched; on, capped at 3 Hz and softened. */
export function capFlash(hz: number, depth: number, reduce: boolean): Flash {
  if (!reduce) return { hz, depth };
  return { hz: Math.min(Math.max(0, hz), FLASH_CAP_HZ), depth: depth * FLASH_SOFTEN };
}

const TAU = Math.PI * 2;

/**
 * A light pulse at time `t` (s), 0..depth: the half-wave the game has always used (a sharp rise every
 * cycle), or, reduced, a smooth raised cosine at the capped rate and the softened depth.
 */
export function flashPulse(t: number, hz: number, depth: number, reduce: boolean): number {
  const f = capFlash(hz, depth, reduce);
  if (reduce) return f.depth * (0.5 - 0.5 * Math.cos(t * TAU * f.hz));
  return f.depth * Math.max(0, Math.sin(t * TAU * f.hz));
}

/** A smooth flicker (a flame's height, a fireball's size, a bolt's crackle), -depth..depth, capped. */
export function flicker(t: number, hz: number, depth: number, reduce: boolean, phase = 0): number {
  const f = capFlash(hz, depth, reduce);
  return f.depth * Math.sin(t * TAU * f.hz + phase);
}

/** An on/off blink at time `t`: shown for the first half of each cycle; `hz` cycles a second, capped. */
export function blinkShown(t: number, hz: number, reduce: boolean): boolean {
  const f = capFlash(hz, 1, reduce);
  if (f.hz <= 0) return true;
  return Math.floor(t * 2 * f.hz) % 2 === 0;
}

/** A one-off flash's strength (a burst's swell, a blast's bloom, a glow's boost above 1): softened. */
export function softFlash(depth: number, reduce: boolean): number {
  return capFlash(0, depth, reduce).depth;
}
